import { useMemo, useState } from "react";
import { GiCrystalGrowth, GiKnightBanner } from "react-icons/gi";
import { useCastle } from "../api/castle";
import {
  useHeroes,
  useLevelUpHero,
  useRecruitHero,
  useStarUpHero,
} from "../api/heroes";
import { useOpenCrystal, type CrystalRollResult } from "../api/crystals";
import {
  HeroCrystalTier,
  HeroCrystalTierName,
  HeroEntry,
  HeroRarityColor,
  HeroRarityRu,
} from "../types/heroes";
import { Loading } from "./Loading";
import { ErrorView } from "./ErrorView";
import { formatNumber } from "../lib/format";
import { hapticImpact, hapticNotification } from "../lib/telegram";

// Universal shard art per rarity — static painted PNG, no animation.
const UniversalShardArt: Record<number, string> = {
  0: "/icons/universal-shards/common.png",
  1: "/icons/universal-shards/rare.png",
  2: "/icons/universal-shards/epic.png",
  3: "/icons/universal-shards/legendary.png",
};

// Hero crystal art per tier — static unframed crystal with alpha-keyed halo.
// Keyed by HeroCrystalTier int (Dim=1, Shining=2, Ancient=3).
const CrystalArt: Record<number, string> = {
  [HeroCrystalTier.Dim]:     "/icons/crystals/dim-bare.png",
  [HeroCrystalTier.Shining]: "/icons/crystals/shining-bare.png",
  [HeroCrystalTier.Ancient]: "/icons/crystals/ancient-bare.png",
};

// Painted portraits for the heroes. Spotlight 12 (1 Legendary + 2 Epic
// per element) use the legacy JPGs sourced from the ElementPicker batch.
// Rare + Common portraits are added per-element as DALL-E collages land.
const HeroPortraitMap: Record<string, string> = {
  // Spotlight (Legendary + Epic) — landscape JPGs from ElementPicker batch.
  "hero-fire-dragonbane":   "/icons/heroes/fire-dragonslayer.jpg",
  "hero-fire-lord":         "/icons/heroes/fire-firelord.jpg",
  "hero-fire-warlord":      "/icons/heroes/fire-firecommander.jpg",
  "hero-water-deepkeeper":  "/icons/heroes/water-deepkeeper.jpg",
  "hero-water-king":        "/icons/heroes/water-seaking.jpg",
  "hero-water-admiral":     "/icons/heroes/water-admiral.jpg",
  "hero-air-stormarchmage": "/icons/heroes/air-stormarchmage.jpg",
  "hero-air-lord":          "/icons/heroes/air-airlord.jpg",
  "hero-air-pilot":         "/icons/heroes/air-pilot.jpg",
  "hero-earth-mother":      "/icons/heroes/earth-earthmother.jpg",
  "hero-earth-stoneguard":  "/icons/heroes/earth-stoneguard.jpg",
  "hero-earth-bergarr":     "/icons/heroes/earth-mountaineer.jpg",

  // Water Rares + Commons — DALL-E 3x3 collage cropped 2026-05-23.
  "hero-water-icemage":     "/icons/heroes/hero-water-icemage.png",
  "hero-water-coral":       "/icons/heroes/hero-water-coral.png",
  "hero-water-icesniper":   "/icons/heroes/hero-water-icesniper.png",
  "hero-water-healer":      "/icons/heroes/hero-water-healer.png",
  "hero-water-shaman":      "/icons/heroes/hero-water-shaman.png",
  "hero-water-strategist":  "/icons/heroes/hero-water-strategist.png",
  "hero-water-sealord":     "/icons/heroes/hero-water-sealord.png",

  // Fire Rares + Commons — DALL-E 3x3 collage cropped 2026-05-23.
  "hero-fire-berserker":    "/icons/heroes/hero-fire-berserker.png",
  "hero-fire-pyromage":     "/icons/heroes/hero-fire-pyromage.png",
  "hero-fire-sniper":       "/icons/heroes/hero-fire-sniper.png",
  "hero-fire-alchemist":    "/icons/heroes/hero-fire-alchemist.png",
  "hero-fire-gunner":       "/icons/heroes/hero-fire-gunner.png",
  "hero-fire-priest":       "/icons/heroes/hero-fire-priest.png",
  "hero-fire-miner":        "/icons/heroes/hero-fire-miner.png",

  // Earth Rares + Commons — DALL-E 3x3 collage cropped 2026-05-23.
  "hero-earth-quakelord":   "/icons/heroes/hero-earth-quakelord.png",
  "hero-earth-archer":      "/icons/heroes/hero-earth-archer.png",
  "hero-earth-rage":        "/icons/heroes/hero-earth-rage.png",
  "hero-earth-lord":        "/icons/heroes/hero-earth-lord.png",
  "hero-earth-rockguard":   "/icons/heroes/hero-earth-rockguard.png",
  "hero-earth-druid":       "/icons/heroes/hero-earth-druid.png",
  "hero-earth-natureheal":  "/icons/heroes/hero-earth-natureheal.png",

  // Air Rares + Commons — DALL-E 3x3 collage cropped 2026-05-23.
  // Purple-toned palette (deviates from blue/silver) — kept as-is, looks
  // more striking and stays consistent across the seven heroes.
  "hero-air-thunder":       "/icons/heroes/hero-air-thunder.png",
  "hero-air-swift":         "/icons/heroes/hero-air-swift.png",
  "hero-air-squall":        "/icons/heroes/hero-air-squall.png",
  "hero-air-skyguard":      "/icons/heroes/hero-air-skyguard.png",
  "hero-air-windleader":    "/icons/heroes/hero-air-windleader.png",
  "hero-air-windmage":      "/icons/heroes/hero-air-windmage.png",
  "hero-air-hawk":          "/icons/heroes/hero-air-hawk.png",
};


export function HeroesScreen() {
  const heroes = useHeroes();
  const castle = useCastle();
  const recruit = useRecruitHero();
  const levelUp = useLevelUpHero();
  const starUp = useStarUpHero();
  const openCrystal = useOpenCrystal();
  const [tab, setTab] = useState<"crystals" | "owned" | "recruit">("crystals");
  const [drop, setDrop] = useState<CrystalRollResult | null>(null);
  // Hero opened in the detail sheet (the bottom-sheet that hosts level-up /
  // star-up / recruit). Stored by id so re-renders after a mutation pick up
  // the fresh shard / xp counts.
  const [selectedHeroId, setSelectedHeroId] = useState<string | null>(null);

  const partitioned = useMemo(() => {
    const owned: HeroEntry[] = [];
    const available: HeroEntry[] = [];
    (heroes.data ?? []).forEach((h) => (h.owned ? owned : available).push(h));
    const byRarity = (a: HeroEntry, b: HeroEntry) => b.rarity - a.rarity;
    owned.sort(byRarity);
    available.sort(byRarity);
    return { owned, available };
  }, [heroes.data]);

  if (heroes.isPending || castle.isPending) {
    return <Loading message="Зову героев..." />;
  }
  if (heroes.isError || castle.isError) {
    return <ErrorView message="Не получилось загрузить героев." />;
  }
  const data = castle.data!;
  const heroCrystals = (data.state.heroCrystals ?? {}) as Record<string, number>;
  const universalShards = (data.state.universalShards ?? {}) as Record<string, number>;

  const errorMsg =
    pickError(recruit.error) ??
    pickError(levelUp.error) ??
    pickError(starUp.error) ??
    pickError(openCrystal.error);

  const runAction = (
    action: { mutate: (id: string) => void; isPending: boolean },
    heroId: string,
  ) => {
    hapticImpact("medium");
    action.mutate(heroId);
  };

  // Open-crystal: fire server request, pop the reward modal as soon as the
  // roll comes back. No phase animation — the tile just disables until the
  // mutation resolves.
  const onOpenCrystal = (tier: number) => {
    hapticImpact("heavy");
    openCrystal.mutate(tier, {
      onSuccess: (res) => {
        hapticNotification("success");
        setDrop(res.drop);
      },
      onError: () => hapticNotification("error"),
    });
  };

  return (
    <div className="flex h-full flex-col">
      <div className="grid grid-cols-3 gap-1 px-3 pt-2">
        <TabButton active={tab === "crystals"} onClick={() => setTab("crystals")}>
          Кристаллы
        </TabButton>
        <TabButton active={tab === "owned"} onClick={() => setTab("owned")}>
          Мои ({partitioned.owned.length})
        </TabButton>
        <TabButton active={tab === "recruit"} onClick={() => setTab("recruit")}>
          Найм
        </TabButton>
      </div>

      {errorMsg && (
        <div className="mx-3 mt-2 rounded-lg bg-tg-destructive/20 p-2 text-sm text-tg-destructive">
          {errorMsg}
        </div>
      )}

      <div className="flex-1 space-y-2 overflow-y-auto px-3 py-3">
        {tab === "crystals" && (
          <CrystalsTab
            crystals={heroCrystals}
            universals={universalShards}
            onOpen={onOpenCrystal}
            opening={openCrystal.isPending}
          />
        )}

        {tab === "owned" &&
          (partitioned.owned.length === 0 ? (
            <div className="rounded-xl bg-tg-secondaryBg p-4 text-center text-sm text-tg-hint">
              Героев пока нет. Открывай кристаллы и собирай осколки —
              когда наберётся достаточно, нанимай героя во вкладке «Найм».
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {partitioned.owned.map((h) => (
                <HeroTile
                  key={h.id}
                  hero={h}
                  onClick={() => {
                    hapticImpact("light");
                    setSelectedHeroId(h.id);
                  }}
                />
              ))}
            </div>
          ))}

        {tab === "recruit" && (
          <div className="grid grid-cols-3 gap-2">
            {partitioned.available.map((h) => (
              <HeroTile
                key={h.id}
                hero={h}
                onClick={() => {
                  hapticImpact("light");
                  setSelectedHeroId(h.id);
                }}
              />
            ))}
          </div>
        )}
      </div>

      {drop && <DropResultModal drop={drop} onClose={() => setDrop(null)} />}

      {selectedHeroId && (() => {
        const h = heroes.data?.find((x) => x.id === selectedHeroId);
        if (!h) return null;
        return (
          <HeroDetailSheet
            hero={h}
            onClose={() => setSelectedHeroId(null)}
            onRecruit={() => runAction(recruit, h.id)}
            onLevelUp={() => runAction(levelUp, h.id)}
            onStarUp={() => runAction(starUp, h.id)}
            recruitPending={recruit.isPending}
            levelUpPending={levelUp.isPending}
            starUpPending={starUp.isPending}
          />
        );
      })()}
    </div>
  );
}

function pickError(err: unknown): string | null {
  if (!err || typeof err !== "object") return null;
  const resp = (err as { response?: { data?: { error?: string } } }).response;
  return resp?.data?.error ?? null;
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg py-1.5 text-sm ${
        active ? "bg-tg-button text-tg-buttonText" : "bg-tg-secondaryBg text-tg-hint"
      }`}
    >
      {children}
    </button>
  );
}

// Three painted phases per tier swap into the tile during the open sequence:
//   idle      — bare crystal + halo on a transparent background; lives the
//               longest, so we use the cleaner unframed art keyed to alpha
//   charging  — server-roundtrip while useOpenCrystal is in flight (framed
//               "phase 2" art from the painted opening-animation sheet)
//   burst     — short window after success, just before the reward modal pops
// Top section of the Crystals tab — your inventory + open buttons + universal-
// shard totals. The roulette result pops in a modal handled at screen level.
function CrystalsTab({
  crystals,
  universals,
  onOpen,
  opening,
}: {
  crystals: Record<string, number>;
  universals: Record<string, number>;
  onOpen: (tier: number) => void;
  opening: boolean;
}) {
  const tiers = [HeroCrystalTier.Dim, HeroCrystalTier.Shining, HeroCrystalTier.Ancient];

  return (
    <>
      <div className="mb-1 text-xs uppercase tracking-wide text-tg-hint">
        Твои кристаллы
      </div>
      <div className="grid grid-cols-3 gap-2">
        {tiers.map((tier) => {
          const count = crystals[String(tier)] ?? 0;
          return (
            <button
              key={tier}
              type="button"
              disabled={count <= 0 || opening}
              onClick={() => onOpen(tier)}
              className="flex flex-col items-center gap-1 overflow-hidden rounded-xl bg-tg-secondaryBg p-2 disabled:opacity-50"
            >
              <img
                src={CrystalArt[tier]}
                alt={HeroCrystalTierName[tier]}
                className="h-32 w-auto object-contain"
                draggable={false}
              />
              <div className="text-xs font-medium leading-tight">
                {HeroCrystalTierName[tier]}
              </div>
              <div className="text-2xl font-bold tabular-nums">{count}</div>
              <div className="text-[10px] text-tg-hint">
                {count > 0 ? "Открыть" : "Нет в наличии"}
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-4 mb-1 text-xs uppercase tracking-wide text-tg-hint">
        Универсальные осколки
      </div>
      <div className="grid grid-cols-4 gap-2 text-center">
        {[0, 1, 2, 3].map((r) => (
          <div key={r} className="rounded-lg bg-tg-secondaryBg p-2">
            <img
              src={UniversalShardArt[r]}
              alt={HeroRarityRu[r]}
              className="mx-auto h-12 w-auto object-contain"
              draggable={false}
            />
            <div className="mt-1 text-[10px] text-tg-hint">{HeroRarityRu[r]}</div>
            <div className="text-base font-medium tabular-nums">
              {formatNumber(universals[String(r)] ?? 0)}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 rounded-xl bg-tg-secondaryBg/60 p-3 text-xs leading-relaxed text-tg-hint">
        Тапни кристалл — он откроется и даст осколки одного из героев твоей
        стихии (или универсальные, если не повезло с конкретным героем).
        Универсальные конвертируются 1:1 в осколки любого героя своей редкости —
        открой нужного героя на вкладке «Найм» и нажми «Превратить».
      </div>
    </>
  );
}

// Reveals what fell out of a crystal. The player taps "ОК" to dismiss; the
// claimed shards have already been credited server-side.
function DropResultModal({
  drop,
  onClose,
}: {
  drop: CrystalRollResult;
  onClose: () => void;
}) {
  const isNamed = drop.kind === "named";
  const rarity = HeroRarityRu[drop.rarity] ?? "?";
  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="animate-pop-in mx-3 max-w-md rounded-2xl bg-tg-secondaryBg p-5 text-center shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-2 text-xs uppercase tracking-wide text-tg-hint">
          Кристалл открыт
        </div>
        <GiCrystalGrowth
          size={64}
          className={`mx-auto animate-element-pulse ${HeroRarityColor[drop.rarity]}`}
        />
        <div className={`mt-2 text-lg font-semibold ${HeroRarityColor[drop.rarity]}`}>
          +{drop.amount} {isNamed ? "осколков" : "универсальных осколков"}
        </div>
        <div className="mt-0.5 text-sm">
          {isNamed
            ? `${drop.heroName ?? "?"} (${rarity})`
            : `${rarity}`}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="mt-4 w-full rounded-lg bg-tg-button px-3 py-2 text-sm font-medium text-tg-buttonText"
        >
          ОК
        </button>
      </div>
    </div>
  );
}

// Square portrait tile used in both "Мои" and "Найм" grids. Painted JPG for
// the spotlight 12, rarity-gradient + element emblem fallback for the rest.
// The bottom strip shows name + stars/level and a progress bar — for
// recruit-side that's the shards-to-unlock bar, for owned-side it's the
// shards-to-next-star bar (or "★ макс" when fully starred).
function HeroTile({ hero, onClick }: { hero: HeroEntry; onClick: () => void }) {
  const portrait = HeroPortraitMap[hero.id];
  const owned = hero.owned;
  // Shard progress: recruit needs `recruitShardCost`, star-up needs
  // `starUpShardsCost`. Both share `currentHeroShards`. When already at max
  // stars on an owned hero, we hide the bar and show "★ макс".
  const target = owned
    ? (hero.canStarUp ? hero.starUpShardsCost : 0)
    : hero.recruitShardCost;
  const showBar = target > 0;
  const pct = showBar
    ? Math.min(100, Math.round((hero.currentHeroShards / target) * 100))
    : 0;
  const barFull = showBar && hero.currentHeroShards >= target;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col overflow-hidden rounded-xl bg-tg-secondaryBg text-left transition-transform active:scale-95"
    >
      <div className="relative aspect-square w-full overflow-hidden bg-tg-bg/50">
        {portrait ? (
          <img
            src={portrait}
            alt={hero.name}
            className="h-full w-full object-cover object-top"
            loading="lazy"
            draggable={false}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-tg-hint/40">
            <GiKnightBanner size={42} />
          </div>
        )}
        {/* Rarity strip — thin colored bar across the bottom of the portrait
            so the player can read rarity at a glance even on a placeholder. */}
        <div
          className={`absolute inset-x-0 bottom-0 h-1 ${
            hero.rarity === 3 ? "bg-yellow-400"
            : hero.rarity === 2 ? "bg-purple-400"
            : hero.rarity === 1 ? "bg-blue-400"
            : "bg-tg-hint/50"
          }`}
        />
      </div>
      <div className="px-2 py-1.5">
        <div className="truncate text-[12px] font-medium leading-tight">
          {hero.name}
        </div>
        <div className={`text-[10px] leading-tight ${HeroRarityColor[hero.rarity]}`}>
          {owned
            ? `${"★".repeat(hero.stars)}${hero.stars < hero.maxStars ? "☆".repeat(hero.maxStars - hero.stars) : ""} · Lv ${hero.level}`
            : HeroRarityRu[hero.rarity]}
        </div>
        {showBar ? (
          <>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-tg-bg">
              <div
                className={`h-full rounded-full ${barFull ? "bg-tg-accent" : "bg-tg-button"}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="mt-0.5 text-right text-[10px] tabular-nums text-tg-hint">
              {hero.currentHeroShards}/{target}
            </div>
          </>
        ) : (
          owned && (
            <div className="mt-1.5 text-[10px] text-tg-hint">★ макс</div>
          )
        )}
      </div>
    </button>
  );
}

// Bottom-sheet that opens when a HeroTile is tapped. Shows the large portrait
// and the full action set — recruit for an unowned hero, level-up / star-up
// for an owned one. Mutations are passed in as callbacks so the sheet stays
// presentation-only.
function HeroDetailSheet({
  hero,
  onClose,
  onRecruit,
  onLevelUp,
  onStarUp,
  recruitPending,
  levelUpPending,
  starUpPending,
}: {
  hero: HeroEntry;
  onClose: () => void;
  onRecruit: () => void;
  onLevelUp: () => void;
  onStarUp: () => void;
  recruitPending: boolean;
  levelUpPending: boolean;
  starUpPending: boolean;
}) {
  const portrait = HeroPortraitMap[hero.id];
  const hallOk = hero.currentHeroHall >= hero.requiresHeroHall;
  const shardsOk = hero.currentHeroShards >= hero.recruitShardCost;
  const canRecruit = hallOk && shardsOk;
  const canAffordLvl = hero.canLevelUp && hero.currentHeroXp >= hero.levelUpXpCost;
  const canAffordStars =
    hero.canStarUp && hero.currentHeroShards >= hero.starUpShardsCost;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full overflow-y-auto rounded-t-2xl bg-tg-bg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative aspect-[4/5] w-full overflow-hidden bg-tg-secondaryBg">
          {portrait ? (
            <img
              src={portrait}
              alt={hero.name}
              className="h-full w-full object-cover object-top"
              draggable={false}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-tg-hint/40">
              <GiKnightBanner size={96} />
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            // Pushed down by safe-area-inset-top + a fixed margin so the
            // Telegram WebApp profile / menu chrome (which floats over the
            // page on mobile) doesn't overlap the close target.
            style={{ top: "calc(env(safe-area-inset-top, 0px) + 0.75rem)" }}
            className="absolute right-3 rounded-full bg-black/60 px-2.5 py-1 text-base text-white shadow"
            aria-label="Закрыть"
          >
            ✕
          </button>
        </div>

        <div className="p-4">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-lg font-semibold">{hero.name}</div>
              <div className={`text-xs ${HeroRarityColor[hero.rarity]}`}>
                {HeroRarityRu[hero.rarity]} · {"★".repeat(hero.stars)}
                {hero.stars < hero.maxStars ? "☆".repeat(hero.maxStars - hero.stars) : ""}
              </div>
            </div>
            {hero.owned && (
              <div className="rounded bg-tg-secondaryBg px-2 py-1 text-xs tabular-nums text-tg-hint">
                Lvl {hero.level}/{hero.maxLevel}
              </div>
            )}
          </div>

          {/* What the hero actually does in battle. Empty for catalog entries
              that have no passives / skills attached. */}
          {(hero.passives?.length || hero.skills?.length) ? (
            <div className="mt-3 rounded-lg bg-tg-secondaryBg p-3">
              <div className="mb-1 text-xs uppercase tracking-wide text-tg-hint">
                Что даёт в бою
              </div>
              {hero.passives && hero.passives.length > 0 && (
                <ul className="space-y-0.5 text-sm">
                  {hero.passives.map((p, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-tg-accent">●</span>
                      <span>{p.summary}</span>
                    </li>
                  ))}
                </ul>
              )}
              {hero.skills && hero.skills.length > 0 && (
                <ul className={`${hero.passives && hero.passives.length > 0 ? "mt-2" : ""} space-y-1 text-sm`}>
                  {hero.skills.map((s) => (
                    <li key={s.id}>
                      <div className="flex items-start gap-2">
                        <span className="text-tg-accent">⚡</span>
                        <div className="flex-1">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="font-medium">{s.name}</span>
                            <span className="text-[10px] text-tg-hint">
                              {s.kind} · КД {s.cooldownRounds}р
                            </span>
                          </div>
                          <div className="text-xs text-tg-hint">{s.description}</div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}

          {!hero.owned ? (
            <>
              <div className="mt-3">
                <div className="flex items-baseline justify-between text-xs">
                  <span className="text-tg-hint">Осколки этого героя</span>
                  <span className="text-tg-hint tabular-nums">
                    {hero.currentHeroShards} / {hero.recruitShardCost}
                  </span>
                </div>
                <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-tg-secondaryBg">
                  <div
                    className={`h-full rounded-full ${shardsOk ? "bg-tg-accent" : "bg-tg-button"}`}
                    style={{
                      width: `${Math.min(100, Math.round((hero.currentHeroShards / hero.recruitShardCost) * 100))}%`,
                    }}
                  />
                </div>
              </div>
              {!hallOk && (
                <div className="mt-2 text-xs text-tg-destructive">
                  Нужен Зал героев Lvl {hero.requiresHeroHall} (у тебя {hero.currentHeroHall})
                </div>
              )}
              <button
                type="button"
                onClick={onRecruit}
                disabled={!canRecruit || recruitPending}
                className="mt-4 w-full rounded-lg bg-tg-button px-3 py-3 text-sm font-medium text-tg-buttonText disabled:opacity-40"
              >
                {recruitPending ? "Найм…" : "Нанять"}
              </button>
            </>
          ) : (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={onLevelUp}
                disabled={!canAffordLvl || levelUpPending}
                className="rounded-lg bg-tg-secondaryBg p-3 text-left text-xs disabled:opacity-40"
              >
                <div className="text-sm font-medium">+Уровень</div>
                {hero.canLevelUp ? (
                  <div className="mt-0.5 text-tg-hint">
                    {formatNumber(hero.levelUpXpCost)} опыта → Lvl {hero.nextLevel}
                  </div>
                ) : (
                  <div className="mt-0.5 text-tg-hint">
                    Потолок (Замок Lvl {hero.currentCastleLevel})
                  </div>
                )}
              </button>
              <button
                type="button"
                onClick={onStarUp}
                disabled={!canAffordStars || starUpPending}
                className="rounded-lg bg-tg-secondaryBg p-3 text-left text-xs disabled:opacity-40"
              >
                <div className="text-sm font-medium">+Звезда</div>
                {hero.canStarUp ? (
                  <div className="mt-0.5 text-tg-hint">
                    {hero.currentHeroShards}/{hero.starUpShardsCost} осколков → ★{hero.nextStar}
                  </div>
                ) : (
                  <div className="mt-0.5 text-tg-hint">★ макс</div>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
